import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { CircularProgress } from "@mui/material";
import { X } from "lucide-react";
import Filters from "../../../../assets/images/filters.svg";
import Threedots from "../../../../assets/images/PhosphorLine.svg";
import Refresh from "../../../../assets/images/ArrowCounterClockwise.svg";
import CalendarIcon from "../../../../assets/images/Calendar.svg";
import MultiSelectWithSearchInput from "Samarth Module/components/dropdown/MultiSelectWithInput";
import {
  fetchDcSupplyInsightsFilters,
  fetchDcSupplyInsightsRows,
  fetchDcSupplyInsightsCount,
  fetchDcSupplyInsightsTotals,
  fetchDcSupplyInsightsNrMitigationTable,
  fetchDcSupplyInsightsHistoricalGraph,
  fetchDcSupplyInsightsNrSufficiencyGraph,
  fetchDcSupplyInsightsAllocToPdqGraph,
  fetchDcSupplyInsightsNrAllocDropGraph,
  fetchDcSupplyInsightsNrDestNrApoDropGraph,
  fetchDcSupplyInsightsBucketsOverviewGraph,
  fetchDcSupplyInsightsExportData,
} from "../../../api/factoryToDcApi";
import OverflowMenu from "Samarth Module/components/overflowMenu/OverflowMenu";
import MoreFilters from "Samarth Module/components/drawers/moreFilters";
import CustomiseColumns from "Samarth Module/components/drawers/CustomiseColumns";
import SnackBarMessage from "Samarth Module/components/snackbar/snackbar";
import styles from "./DcSupplyInsights.module.css";
import newExcel from "../../../../assets/images/FileXls.svg";
import Expand from "../../../../assets/images/expand.svg";
import CustomButton from "Samarth Module/components/buttons/CustomButton";
import DcSupplyInsightsOverviewTable from "./DcSupplyInsightsOverviewTable";
import BucketsOfAllocation from "./BucketsOfAllocation";
import HistoricalDataGraph from "./HistoricalDataGraph";
import NrSufficiencyGraph from "./NrSufficiencyGraph";
import AllocToPdqGraph from "./AllocToPdqGraph";
import NrAllocDropGraph from "./NrAllocDropGraph";
import NrDestNrApoDropGraph from "./NrDestNrApoDropGraph";
import BucketsOverviewGraph from "./BucketsOverviewGraph";

const DEFAULT_ROWS_PER_PAGE = 15;
const PDQ_PAGE_SIZE = 15;

const isAbortError = (error) =>
  error?.name === "AbortError" ||
  error?.message?.toLowerCase?.().includes("aborted");

const buildSelectedFilterPayload = (sourceFilters = []) =>
  sourceFilters.reduce((acc, filter) => {
    acc[filter.label] = filter.selectedData;
    return acc;
  }, {});

const getSelectedValues = (selectedData) => {
  if (Array.isArray(selectedData)) {
    return selectedData;
  }

  if (selectedData) {
    return [selectedData];
  }

  return [];
};

const getTotalRow = (totalsData) => {
  if (!totalsData) {
    return {};
  }

  if (Array.isArray(totalsData?.total_data)) {
    return totalsData.total_data[0] || {};
  }

  return totalsData?.total_data || {};
};

const handleOptionalApiError = (error, apiName) => {
  if (isAbortError(error)) {
    return null;
  }

  console.error(`${apiName} API error:`, error);
  return undefined;
};

const buildTablePayload = ({
  activeAccount,
  activeTab,
  sourceFilters,
  uom,
  page,
  rowsPerPage,
  sortBy,
  sortOrder,
  fieldsection,
}) => {
  const payload = {
    email: activeAccount.username,
    uom,
    filters: buildSelectedFilterPayload(sourceFilters),
    type: "Dc Supply Insights",
    overviewtype: activeTab,
    pagination: {
      page,
      rowsPerPage: rowsPerPage || DEFAULT_ROWS_PER_PAGE,
    },
    sorting: {
      sortBy,
      sortOrder,
      fieldsection,
    },
  };

  return payload;
};

const OverviewTabs = ({ selectedTab, onTabChange }) => {
  const tabs = [
    "Summary",
    "Order Loss NR Sufficiency",
    "NR Propogation Loss",
    "NR - Alloc % Drop",
    "Alloc to PDQ",
  ];

  return (
    <div
      style={{
        display: "flex",
        gap: "10px",
        padding: "5px",
        border: "1px solid #E5E7EB",
        borderRadius: "10px",
        backgroundColor: "rgb(246 246 246)",
      }}
    >
      {tabs.map((tab) => (
        <button
          key={tab}
          type="button"
          onClick={() => onTabChange(tab)}
          style={{
            padding: "6px 14px",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "12px",
            border:
              selectedTab === tab ? "1px solid #3447d4" : "1px solid #E5E7EB",
            background: selectedTab === tab ? "#EEF2FF" : "#fff",
            color: selectedTab === tab ? "#3447d4" : "#555",
            fontWeight: selectedTab === tab ? 600 : 400,
          }}
        >
          {tab}
        </button>
      ))}
    </div>
  );
};

OverviewTabs.propTypes = {
  selectedTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
};

const mergeFiltersResponse = (incoming, previous) => {
  const incomingList = Array.isArray(incoming) ? incoming : [];
  if (!Array.isArray(previous) || previous.length === 0) {
    return incomingList;
  }
  const prevByLabel = new Map(previous.map((f) => [f?.label, f]));
  return incomingList.map((f) => {
    const prev = prevByLabel.get(f?.label);
    if (!prev) return f;
    return {
      ...f,
      selectedData: prev.selectedData ?? f.selectedData,
    };
  });
};

const getGraphTypeForTab = (tab, propagationGraphTab) => {
  if (tab === "Order Loss NR Sufficiency") {
    return "nr_sufficiency_graph";
  }
  if (tab === "NR - Alloc % Drop") {
    return "nr_alloc_drop_graph";
  }
  if (tab === "Alloc to PDQ") {
    return "alloc_to_pdq_graph";
  }
  if (tab === "NR Propogation Loss") {
    if (propagationGraphTab === "Buckets overview") {
      return "buckets_overview_graph";
    }
    return "nr_dest_nr_apo_drop_graph";
  }
  return "historical_graph";
};

const getGraphTitleForTab = (tab, propagationGraphTab) => {
  if (tab === "Order Loss NR Sufficiency") {
    return "NR Sufficiency";
  }
  if (tab === "NR - Alloc % Drop") {
    return "NR APO to Alloc at source";
  }
  if (tab === "Alloc to PDQ") {
    return "Alloc To PDQ";
  }
  if (tab === "NR Propogation Loss") {
    if (propagationGraphTab === "NR at Dest vs nr apo drop") {
      return "NR at Dest vs nr apo drop";
    }
    return "Buckets overview";
  }
  return "Historical data (NR to PDQ)";
};

const HistoricalCalendarPopover = ({
  pendingHistoricalDateRange,
  parseYYYYMMDD,
  formatDateToYYYYMMDD,
  isHistoricalDateAvailable,
  handleHistoricalDateClick,
  styles,
}) => {
  const [viewMonth, setViewMonth] = useState(() => {
    if (pendingHistoricalDateRange.startDate) {
      const d = parseYYYYMMDD(pendingHistoricalDateRange.startDate);
      return d || new Date();
    }
    return new Date();
  });

  const buildCalendarGrid = () => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const grid = [];
    let week = [];

    for (let i = 0; i < startDayOfWeek; i++) {
      week.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = formatDateToYYYYMMDD(new Date(year, month, day));
      week.push(dateString);

      if (week.length === 7) {
        grid.push(week);
        week = [];
      }
    }

    if (week.length > 0) {
      while (week.length < 7) {
        week.push(null);
      }
      grid.push(week);
    }

    return grid;
  };

  const grid = buildCalendarGrid();

  const handlePrevMonth = () => {
    setViewMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setViewMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1),
    );
  };

  const monthYearLabel = viewMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const isDateSelected = (dateString) => {
    if (!dateString) return false;
    const pending = pendingHistoricalDateRange;
    if (pending.startDate === dateString || pending.endDate === dateString) {
      return true;
    }
    if (pending.startDate && pending.endDate) {
      const start = parseYYYYMMDD(pending.startDate);
      const end = parseYYYYMMDD(pending.endDate);
      const current = parseYYYYMMDD(dateString);
      if (start && end && current) {
        const normalizedStart = new Date(
          Math.min(start.getTime(), end.getTime()),
        );
        const normalizedEnd = new Date(
          Math.max(start.getTime(), end.getTime()),
        );
        return current >= normalizedStart && current <= normalizedEnd;
      }
    }
    return false;
  };

  const renderEmptyCalendarDay = (year, month, weekIdx, dayIdx) => (
    <div
      key={`empty-${year}-${month}-${weekIdx}-${dayIdx}`}
      className={styles.calendarDayEmpty}
    />
  );

  const renderCalendarDayButton = (dateString) => {
    const isAvailable = isHistoricalDateAvailable(dateString);
    const isSelected = isDateSelected(dateString);

    return (
      <button
        key={dateString}
        type="button"
        className={`${styles.calendarDay} ${
          isSelected ? styles.calendarDaySelected : ""
        } ${isAvailable ? "" : styles.calendarDayDisabled}`}
        disabled={!isAvailable}
        onClick={() => handleHistoricalDateClick(dateString)}
        aria-label={dateString}
      >
        {new Date(dateString).getDate()}
      </button>
    );
  };

  const renderCalendarDay = (dateString, year, month, weekIdx, dayIdx) => {
    if (dateString) {
      return renderCalendarDayButton(dateString);
    }
    return renderEmptyCalendarDay(year, month, weekIdx, dayIdx);
  };

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();

  return (
    <div className={styles.historicalCalendarPopover}>
      <div className={styles.calendarHeader}>
        <button
          type="button"
          className={styles.calendarNavButton}
          onClick={handlePrevMonth}
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className={styles.calendarMonthYear}>{monthYearLabel}</span>
        <button
          type="button"
          className={styles.calendarNavButton}
          onClick={handleNextMonth}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className={styles.calendarDaysOfWeek}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div key={day} className={styles.calendarDayOfWeek}>
            {day}
          </div>
        ))}
      </div>

      <div className={styles.calendarGrid}>
        {grid.map((week, weekIdx) => (
          <div
            key={`week-${year}-${month}-${weekIdx}`}
            className={styles.calendarWeek}
          >
            {week.map((dateString, dayIdx) =>
              renderCalendarDay(dateString, year, month, weekIdx, dayIdx),
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

HistoricalCalendarPopover.propTypes = {
  pendingHistoricalDateRange: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }).isRequired,
  parseYYYYMMDD: PropTypes.func.isRequired,
  formatDateToYYYYMMDD: PropTypes.func.isRequired,
  isHistoricalDateAvailable: PropTypes.func.isRequired,
  handleHistoricalDateClick: PropTypes.func.isRequired,
  styles: PropTypes.objectOf(PropTypes.string).isRequired,
};

const DcSupplyInsights = ({ sideViewTab }) => {
  const abortControllersRef = useRef({
    table: null,
    pdq: null,
    historical: null,
    nrSufficiency: null,
    allocToPdq: null,
    nrAllocDrop: null,
    nrDestNrApoDrop: null,
    bucketsOverview: null,
    filters: null,
  });

  const cancelPrevious = (key) => {
    abortControllersRef.current[key]?.abort();
    abortControllersRef.current[key] = new AbortController();
    return abortControllersRef.current[key].signal;
  };

  const pendingLoadAllApisRef = useRef(null);
  const LOAD_ALL_APIS_DEBOUNCE_MS = 800;

  const pendingHeavyApisTimerRef = useRef(null);
  const latestLoadIntentRef = useRef(0);

  const clearDeferredApiTimers = () => {
    if (pendingHeavyApisTimerRef.current) {
      clearTimeout(pendingHeavyApisTimerRef.current);
      pendingHeavyApisTimerRef.current = null;
    }
  };

  const handleNrPropagationLossGraphs = (currentFilters, uom) => {
    if (selectedPropagationGraphTab === "NR at Dest vs nr apo drop") {
      fetchNrDestNrApoDropGraphData(currentFilters, uom, selectedStamping);
      return;
    }

    if (selectedPropagationGraphTab === "Buckets overview") {
      fetchBucketsOverviewGraphData(currentFilters, uom);
      return;
    }

    setNrDestNrApoDropData([]);
    setNrDestNrApoDropLoading(false);
    setBucketsOverviewData([]);
    setBucketsOverviewLoading(false);
  };

  const fetchGraphDataForTab = (tab, currentFilters, uom, lossType) => {
    if (tab === "Order Loss NR Sufficiency") {
      fetchNrSufficiencyGraphData(currentFilters, uom);
      return;
    }

    if (tab === "NR - Alloc % Drop") {
      fetchNrAllocDropGraphData(currentFilters, uom, selectedDropLossType);
      return;
    }

    if (tab === "Alloc to PDQ") {
      fetchAllocToPdqGraphData(currentFilters, uom, lossType);
      return;
    }

    if (tab === "NR Propogation Loss") {
      handleNrPropagationLossGraphs(currentFilters, uom);
      return;
    }

    fetchHistoricalGraphData(currentFilters, uom);
  };

  const scheduleHeavyApis = (
    currentFilters,
    uom,
    loadIntentId,
    tab = activeTab,
    lossType = selectedLossType,
  ) => {
    if (pendingHeavyApisTimerRef.current) {
      clearTimeout(pendingHeavyApisTimerRef.current);
      pendingHeavyApisTimerRef.current = null;
    }

    pendingHeavyApisTimerRef.current = setTimeout(() => {
      pendingHeavyApisTimerRef.current = null;

      if (loadIntentId !== latestLoadIntentRef.current) return;

      fetchPdqAllocationData(currentFilters, uom);
      fetchGraphDataForTab(tab, currentFilters, uom, lossType);
    }, 1200);
  };

  const scheduleLoadAllApis = (
    currentFilters,
    uom,
    options,
    delayMs = LOAD_ALL_APIS_DEBOUNCE_MS,
  ) => {
    if (pendingLoadAllApisRef.current) {
      clearTimeout(pendingLoadAllApisRef.current);
      pendingLoadAllApisRef.current = null;
    }

    clearDeferredApiTimers();

    if (!delayMs) {
      loadAllApis(currentFilters, uom, options);
      return;
    }

    pendingLoadAllApisRef.current = setTimeout(() => {
      pendingLoadAllApisRef.current = null;
      loadAllApis(currentFilters, uom, options);
    }, delayMs);
  };

  const requestRefs = {
    table: useRef(0),
    pdq: useRef(0),
    historical: useRef(0),
    nrSufficiency: useRef(0),
    allocToPdq: useRef(0),
    nrAllocDrop: useRef(0),
    nrDestNrApoDrop: useRef(0),
    bucketsOverview: useRef(0),
    filters: useRef(0),
  };

  const { accounts } = useMsal();
  const activeAccount = accounts[0];

  const [isDrawerOpen, setIsDrawerOpen] = useState({
    filterDrawer: false,
    customiseDrawer: false,
  });
  const [tableData, setTableData] = useState([]);

  const [paginationData, setPaginationData] = useState(null);
  const [sortingData, setSortingData] = useState({
    sortBy: "",
    sortOrder: "DESC",
    fieldsection: "",
  });

  const [filters, setFilters] = useState({
    dcSupplyInsightsFiltersData: [],

    dcSupplyInsightsColumnsMap: {},
    customizedColumnsMap: {},
  });

  const [selectedUOM, setSelectedUOM] = useState("Tonnes");

  const [loading, setLoading] = useState({
    initialLoading: true,
    dataLoading: false,
    tableBodyLoading: false,
    exportLoading: false,
    summaryLoading: false,
  });

  const [filtersLoading, setFiltersLoading] = useState(false);

  const [snackBarObject, setSnackBarObject] = useState({
    snackbarOpen: false,
    message: "",
    type: "error",
  });

  const [showExpanded, setShowExpanded] = useState(true);
  const [expandedGraph, setExpandedGraph] = useState(null);

  const uomOptions = ["Cases", "Tonnes", "Eaches"];

  const [activeTab, setActiveTab] = useState("Summary");

  const [pdqTableData, setPdqTableData] = useState([]);
  const [pdqLoading, setPdqLoading] = useState(false);
  const [pdqLoadMoreLoading, setPdqLoadMoreLoading] = useState(false);
  const [pdqHasMore, setPdqHasMore] = useState(false);
  const [pdqSortingData, setPdqSortingData] = useState({
    sortBy: "BG",
    sortOrder: "ASC",
  });

  const pdqNextCursorRef = useRef(null);
  const pdqHasMoreRef = useRef(false);
  const pdqLoadMoreRef = useRef(false);

  const [historicalData, setHistoricalData] = useState([]);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [historicalAvailableDates, setHistoricalAvailableDates] = useState([]);
  const [historicalDateRange, setHistoricalDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [pendingHistoricalDateRange, setPendingHistoricalDateRange] = useState({
    startDate: "",
    endDate: "",
  });
  const [isHistoricalCalendarOpen, setIsHistoricalCalendarOpen] =
    useState(false);

  const [nrSufficiencyData, setNrSufficiencyData] = useState([]);
  const [nrSufficiencyLoading, setNrSufficiencyLoading] = useState(false);

  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const [fallbackColumns, setFallbackColumns] = useState([]);

  const [allocToPdqData, setAllocToPdqData] = useState([]);
  const [allocToPdqLoading, setAllocToPdqLoading] = useState(false);

  const [selectedLossType, setSelectedLossType] = useState("Master Loss");

  const allocToPdqLossOptions = [
    "Master Loss",
    "ECC Loss",
    "OTM Drops",
    "Loadability",
    "Value Drop",
    "Loadability 7 Value Drop",
  ];

  const [nrAllocDropData, setNrAllocDropData] = useState([]);
  const [nrAllocDropLoading, setNrAllocDropLoading] = useState(false);

  const [selectedDropLossType, setSelectedDropLossType] =
    useState("Stock Issue Loss");

  const nrAllocDropLossOptions = [
    "Stock Issue Loss",
    "QC Pending Loss",
    "Reservation Loss",
    "Open PO Loss",
    "IC Master",
  ];

  const [nrDestNrApoDropData, setNrDestNrApoDropData] = useState([]);
  const [nrDestNrApoDropLoading, setNrDestNrApoDropLoading] = useState(false);

  const [selectedPropagationGraphTab, setSelectedPropagationGraphTab] =
    useState("NR at Dest vs nr apo drop");

  const [selectedStamping, setSelectedStamping] =
    useState("Source not defined");

  const stampingOptions = [
    "Source not defined",
    "Lane Blocked",
    "Manual N* maintained",
    "SNP Planner F*",
    "Aggregation Flag Not Selected",
    "Unaccounted",
    "Multiple Stamping",
  ];

  const [bucketsOverviewData, setBucketsOverviewData] = useState([]);
  const [bucketsOverviewLoading, setBucketsOverviewLoading] = useState(false);

  const calendarWrapperRef = useRef(null);

  // Helper functions for historical calendar date management
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";
    if (typeof date === "string") return date;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const parseYYYYMMDD = (dateString) => {
    if (!dateString || typeof dateString !== "string") return null;
    const parts = dateString.split("-");
    if (parts.length !== 3) return null;
    const year = Number.parseInt(parts[0], 10);
    const month = Number.parseInt(parts[1], 10) - 1;
    const day = Number.parseInt(parts[2], 10);
    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day))
      return null;
    return new Date(year, month, day);
  };

  const isHistoricalDateAvailable = useCallback(
    (dateString) => {
      if (!dateString) return false;
      return historicalAvailableDates.includes(dateString);
    },
    [historicalAvailableDates],
  );

  const normalizeHistoricalRange = (startDate, endDate) => {
    if (!startDate || !endDate) {
      return { startDate, endDate };
    }

    const start =
      typeof startDate === "string" ? parseYYYYMMDD(startDate) : startDate;
    const end = typeof endDate === "string" ? parseYYYYMMDD(endDate) : endDate;

    if (!start || !end) {
      return { startDate, endDate };
    }

    if (start > end) {
      return {
        startDate: formatDateToYYYYMMDD(end),
        endDate: formatDateToYYYYMMDD(start),
      };
    }

    return {
      startDate: formatDateToYYYYMMDD(start),
      endDate: formatDateToYYYYMMDD(end),
    };
  };

  const hasValidHistoricalSelection = (range) => {
    return range?.startDate && range?.endDate;
  };

  const isSameHistoricalRange = (current, next) => {
    if (!current && !next) return true;
    if (!current || !next) return false;
    return (
      current.startDate === next.startDate && current.endDate === next.endDate
    );
  };

  const handleHistoricalCalendarApplyAndClose = useCallback(() => {
    setIsHistoricalCalendarOpen(false);

    const pending = pendingHistoricalDateRange;

    // If no dates selected in pending, just close without triggering API
    if (!pending.startDate && !pending.endDate) {
      // Reset to default state when closing without selection
      setPendingHistoricalDateRange({ startDate: "", endDate: "" });
      return;
    }

    let finalRange = { ...pending };

    if (pending.startDate && !pending.endDate) {
      finalRange.endDate = pending.startDate;
    }

    const normalized = normalizeHistoricalRange(
      finalRange.startDate,
      finalRange.endDate,
    );

    if (!hasValidHistoricalSelection(normalized)) {
      setPendingHistoricalDateRange({ startDate: "", endDate: "" });
      return;
    }

    // Only trigger API if the date range has changed
    if (!isSameHistoricalRange(historicalDateRange, normalized)) {
      setHistoricalDateRange(normalized);
      fetchHistoricalGraphData(
        filters.dcSupplyInsightsFiltersData,
        selectedUOM,
        normalized,
      );
    }

    // Reset to default state for next time calendar opens
    setPendingHistoricalDateRange({ startDate: "", endDate: "" });
  }, [
    pendingHistoricalDateRange,
    historicalDateRange,
    filters.dcSupplyInsightsFiltersData,
    selectedUOM,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isHistoricalCalendarOpen &&
        calendarWrapperRef.current &&
        !calendarWrapperRef.current.contains(event.target)
      ) {
        handleHistoricalCalendarApplyAndClose();
      }
    };

    if (isHistoricalCalendarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isHistoricalCalendarOpen, handleHistoricalCalendarApplyAndClose]);

  const handleHistoricalCalendarToggle = () => {
    setIsHistoricalCalendarOpen((prev) => !prev);
    // Always reset pending range to empty when opening calendar
    if (!isHistoricalCalendarOpen) {
      setPendingHistoricalDateRange({
        startDate: "",
        endDate: "",
      });
    }
  };

  const handleHistoricalDateClick = (dateString) => {
    if (!isHistoricalDateAvailable(dateString)) return;

    setPendingHistoricalDateRange((prev) => {
      if (!prev.startDate || (prev.startDate && prev.endDate)) {
        return { startDate: dateString, endDate: "" };
      }

      return { startDate: prev.startDate, endDate: dateString };
    });
  };

  const resetHistoricalDateSelection = () => {
    setHistoricalDateRange({ startDate: "", endDate: "" });
    setPendingHistoricalDateRange({ startDate: "", endDate: "" });
  };

  const resetPdqInfiniteScrollState = () => {
    setPdqTableData([]);
    setPdqHasMore(false);
    pdqHasMoreRef.current = false;
    pdqNextCursorRef.current = null;
  };

  const updatePdqDataAfterFetch = (newRows, nextCursor, hasMore, isAppend) => {
    setPdqTableData((prev) => {
      if (!isAppend) return newRows;

      const existingKeys = new Set(
        prev.map(
          (row) =>
            `${row?.BG || ""}__${row?.["Small C"] || ""}__${row?.Source || ""}`,
        ),
      );

      const uniqueNewRows = newRows.filter((row) => {
        const key = `${row?.BG || ""}__${row?.["Small C"] || ""}__${row?.Source || ""}`;
        return !existingKeys.has(key);
      });

      return [...prev, ...uniqueNewRows];
    });

    setPdqHasMore(hasMore);
    pdqHasMoreRef.current = hasMore;
    pdqNextCursorRef.current = nextCursor;
  };

  const fetchPdqAllocationData = async (
    dcSupplyInsightsFilters,
    uom,
    options,
  ) => {
    const safeFilters = dcSupplyInsightsFilters || [];
    const safeUom = uom || selectedUOM;
    const safeOptions = options || {};

    const isAppend = Boolean(safeOptions.append);
    const requestId = ++requestRefs.pdq.current;

    const safeSorting = safeOptions.sorting || pdqSortingData;

    if (isAppend) {
      if (pdqLoadMoreRef.current) return;
      if (!pdqHasMoreRef.current) return;

      pdqLoadMoreRef.current = true;
      setPdqLoadMoreLoading(true);
    } else {
      setPdqLoading(true);
      resetPdqInfiniteScrollState();
    }

    try {
      const signal = cancelPrevious("pdq");

      const payload = {
        email: activeAccount.username,
        uom: safeUom,
        filters: buildSelectedFilterPayload(safeFilters),
        type: "Dc Supply Insights",
        page_size: PDQ_PAGE_SIZE,
        cursor: isAppend ? safeOptions.cursor : null,
        sorting: {
          sortBy: safeSorting.sortBy,
          sortOrder: safeSorting.sortOrder,
        },
      };

      const res = await fetchDcSupplyInsightsNrMitigationTable(payload, signal);

      if (requestId !== requestRefs.pdq.current) return;

      const newRows = res?.data || [];
      const nextCursor = res?.next_cursor || null;
      const hasMore = Boolean(res?.has_more);

      updatePdqDataAfterFetch(newRows, nextCursor, hasMore, isAppend);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("PDQ Allocation API error:", error);
      }
    } finally {
      if (requestId === requestRefs.pdq.current) {
        if (isAppend) {
          pdqLoadMoreRef.current = false;
          setPdqLoadMoreLoading(false);
        } else {
          setPdqLoading(false);
        }
      }
    }
  };

  const handlePdqLoadMore = useCallback(() => {
    if (pdqLoadMoreRef.current) return;
    if (!pdqHasMoreRef.current) return;
    if (!pdqNextCursorRef.current) return;

    fetchPdqAllocationData(filters.dcSupplyInsightsFiltersData, selectedUOM, {
      append: true,
      cursor: pdqNextCursorRef.current,
      sorting: pdqSortingData,
    });
  }, [filters.dcSupplyInsightsFiltersData, selectedUOM, pdqSortingData]);

  const handlePdqSortChange = useCallback(
    (sortBy, sortOrder) => {
      const newSorting = { sortBy, sortOrder };
      setPdqSortingData(newSorting);
      fetchPdqAllocationData(filters.dcSupplyInsightsFiltersData, selectedUOM, {
        append: false,
        cursor: null,
        sorting: newSorting,
      });
    },
    [filters.dcSupplyInsightsFiltersData, selectedUOM],
  );

  const fetchHistoricalGraphData = async (
    dcSupplyInsightsFilters = [],
    uom = selectedUOM,
    dateRange = null,
  ) => {
    const requestId = ++requestRefs.historical.current;
    setHistoricalLoading(true);

    try {
      const signal = cancelPrevious("historical");
      const payload = {
        email: activeAccount.username,
        uom: uom,
        filters: dcSupplyInsightsFilters.reduce((acc, filter) => {
          acc[filter.label] = filter.selectedData;
          return acc;
        }, {}),
      };

      if (dateRange?.startDate && dateRange?.endDate) {
        payload.start_date = dateRange.startDate;
        payload.end_date = dateRange.endDate;
      }

      const res = await fetchDcSupplyInsightsHistoricalGraph(payload, signal);

      if (requestId !== requestRefs.historical.current) return;

      setHistoricalData(res?.data || []);

      if (!dateRange || (!dateRange.startDate && !dateRange.endDate)) {
        const availableDates = (res?.data || [])
          .map((item) => item.IngestedDate)
          .filter(Boolean);
        setHistoricalAvailableDates(availableDates);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Historical Graph API error:", error);
      }
    } finally {
      if (requestId === requestRefs.historical.current) {
        setHistoricalLoading(false);
      }
    }
  };

  const fetchNrSufficiencyGraphData = async (
    dcSupplyInsightsFilters = [],
    uom = selectedUOM,
  ) => {
    const requestId = ++requestRefs.nrSufficiency.current;

    setNrSufficiencyLoading(true);

    try {
      const signal = cancelPrevious("nrSufficiency");

      const payload = {
        email: activeAccount.username,
        uom,
        filters: dcSupplyInsightsFilters.reduce((acc, filter) => {
          acc[filter.label] = filter.selectedData;
          return acc;
        }, {}),
      };

      const res = await fetchDcSupplyInsightsNrSufficiencyGraph(
        payload,
        signal,
      );

      if (requestId !== requestRefs.nrSufficiency.current) {
        return;
      }

      setNrSufficiencyData(res?.data || []);
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("NR Sufficiency Graph API error:", error);
      }
    } finally {
      if (requestId === requestRefs.nrSufficiency.current) {
        setNrSufficiencyLoading(false);
      }
    }
  };

  const fetchAllocToPdqGraphData = async (
    dcSupplyInsightsFilters = [],
    uom = selectedUOM,
    lossType = selectedLossType,
  ) => {
    const requestId = ++requestRefs.allocToPdq.current;

    setAllocToPdqLoading(true);

    try {
      const signal = cancelPrevious("allocToPdq");

      const payload = {
        email: activeAccount.username,
        uom,
        filters: buildSelectedFilterPayload(dcSupplyInsightsFilters),
        Losses: lossType,
      };

      const res = await fetchDcSupplyInsightsAllocToPdqGraph(payload, signal);

      if (requestId !== requestRefs.allocToPdq.current) {
        return;
      }

      setAllocToPdqData(res?.data || []);
    } catch (error) {
      if (!isAbortError(error)) {
        console.error("Alloc To PDQ Graph API error:", error);
      }
    } finally {
      if (requestId === requestRefs.allocToPdq.current) {
        setAllocToPdqLoading(false);
      }
    }
  };

  const fetchNrAllocDropGraphData = async (
    dcSupplyInsightsFilters = [],
    uom = selectedUOM,
    dropLossType = selectedDropLossType,
  ) => {
    const requestId = ++requestRefs.nrAllocDrop.current;

    setNrAllocDropLoading(true);

    try {
      const signal = cancelPrevious("nrAllocDrop");

      const payload = {
        email: activeAccount.username,
        uom,
        filters: buildSelectedFilterPayload(dcSupplyInsightsFilters),
        "drop loss": dropLossType,
      };

      const res = await fetchDcSupplyInsightsNrAllocDropGraph(payload, signal);

      if (requestId !== requestRefs.nrAllocDrop.current) {
        return;
      }

      setNrAllocDropData(res?.data || []);
    } catch (error) {
      if (!isAbortError(error)) {
        console.error("NR Alloc Drop Graph API error:", error);
      }
    } finally {
      if (requestId === requestRefs.nrAllocDrop.current) {
        setNrAllocDropLoading(false);
      }
    }
  };

  const fetchNrDestNrApoDropGraphData = async (
    dcSupplyInsightsFilters = [],
    uom = selectedUOM,
    stamping = selectedStamping,
  ) => {
    const requestId = ++requestRefs.nrDestNrApoDrop.current;

    setNrDestNrApoDropLoading(true);

    try {
      const signal = cancelPrevious("nrDestNrApoDrop");

      const payload = {
        email: activeAccount.username,
        uom,
        filters: buildSelectedFilterPayload(dcSupplyInsightsFilters),
        stamping,
      };

      const res = await fetchDcSupplyInsightsNrDestNrApoDropGraph(
        payload,
        signal,
      );

      if (requestId !== requestRefs.nrDestNrApoDrop.current) {
        return;
      }

      setNrDestNrApoDropData(res?.data || []);
    } catch (error) {
      if (!isAbortError(error)) {
        console.error("NR Dest NR APO Drop Graph API error:", error);
      }
    } finally {
      if (requestId === requestRefs.nrDestNrApoDrop.current) {
        setNrDestNrApoDropLoading(false);
      }
    }
  };

  const fetchBucketsOverviewGraphData = async (
    dcSupplyInsightsFilters = [],
    uom = selectedUOM,
  ) => {
    const requestId = ++requestRefs.bucketsOverview.current;

    setBucketsOverviewLoading(true);

    try {
      const signal = cancelPrevious("bucketsOverview");

      const payload = {
        email: activeAccount.username,
        uom,
        filters: buildSelectedFilterPayload(dcSupplyInsightsFilters),
      };

      const res = await fetchDcSupplyInsightsBucketsOverviewGraph(
        payload,
        signal,
      );

      if (requestId !== requestRefs.bucketsOverview.current) {
        return;
      }

      setBucketsOverviewData(res?.data || []);
    } catch (error) {
      if (!isAbortError(error)) {
        console.error("Buckets Overview Graph API error:", error);
      }
    } finally {
      if (requestId === requestRefs.bucketsOverview.current) {
        setBucketsOverviewLoading(false);
      }
    }
  };

  const fetchData = async (
    dcSupplyInsightsFilters = [],
    page = 1,
    sortBy = "",
    sortOrder = "",
    uom = selectedUOM,
    rowsPerPage = DEFAULT_ROWS_PER_PAGE,
    fieldsection = "",
  ) => {
    const requestId = ++requestRefs.table.current;
    const isFilterChange = page === 1 && sortBy === "";

    setLoading((prev) => ({
      ...prev,
      dataLoading: isFilterChange,
      tableBodyLoading: !isFilterChange,
      summaryLoading: true,
    }));

    try {
      const signal = cancelPrevious("table");

      const payload = buildTablePayload({
        activeAccount,
        activeTab,
        sourceFilters: dcSupplyInsightsFilters,
        uom,
        page,
        rowsPerPage,
        sortBy,
        sortOrder,
        fieldsection,
      });

      const rowsPromise = fetchDcSupplyInsightsRows(payload, signal);

      const countPromise = fetchDcSupplyInsightsCount(payload, signal).catch(
        (error) => handleOptionalApiError(error, "Count"),
      );

      const totalsPromise = fetchDcSupplyInsightsTotals(payload, signal).catch(
        (error) => handleOptionalApiError(error, "Totals"),
      );

      let rowsData;

      try {
        rowsData = await rowsPromise;
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        throw error;
      }

      if (requestId !== requestRefs.table.current) {
        return;
      }

      if (rowsData?.defaultColumns) {
        setFallbackColumns(rowsData.defaultColumns);
      }

      const rows = rowsData?.data || [];

      setSortingData({
        sortBy: rowsData?.sorting?.sortBy || sortBy,
        sortOrder: rowsData?.sorting?.sortOrder || sortOrder,
        fieldsection: rowsData?.sorting?.fieldsection || fieldsection,
      });

      setTableData([{}, ...rows]);

      setLoading((prev) => ({
        ...prev,
        dataLoading: false,
        tableBodyLoading: false,
      }));

      const [countData, totalsData] = await Promise.all([
        countPromise,
        totalsPromise,
      ]);

      if (requestId !== requestRefs.table.current) {
        return;
      }

      if (countData?.pagination) {
        setPaginationData(countData.pagination);
      }

      setTableData([getTotalRow(totalsData), ...rows]);
    } catch (error) {
      if (!isAbortError(error)) {
        console.error("Error fetching data:", error);
      }

      if (requestId === requestRefs.table.current) {
        setTableData([]);
      }
    } finally {
      if (requestId === requestRefs.table.current) {
        setLoading((prev) => ({
          ...prev,
          dataLoading: false,
          tableBodyLoading: false,
          summaryLoading: false,
        }));
      }
    }
  };
  useEffect(() => {
    if (!activeAccount?.username) return;

    loadAllApis([], selectedUOM, {
      skipHeavyApis: false,
      skipFilters: false,
    });
  }, []);

  useEffect(() => {
    return () => {
      if (pendingLoadAllApisRef.current) {
        clearTimeout(pendingLoadAllApisRef.current);
        pendingLoadAllApisRef.current = null;
      }

      clearDeferredApiTimers();

      Object.values(abortControllersRef.current).forEach((controller) => {
        controller?.abort();
      });
    };
  }, []);

  const fetchGraphForTab = (currentTab, currentFilters, currentUom) => {
    if (currentTab === "Order Loss NR Sufficiency") {
      fetchNrSufficiencyGraphData(currentFilters, currentUom);
    } else if (currentTab === "NR - Alloc % Drop") {
      setSelectedDropLossType("Stock Issue Loss");
      fetchNrAllocDropGraphData(currentFilters, currentUom, "Stock Issue Loss");
    } else if (currentTab === "Alloc to PDQ") {
      setSelectedLossType("Master Loss");
      fetchAllocToPdqGraphData(currentFilters, currentUom, "Master Loss");
    } else if (currentTab === "NR Propogation Loss") {
      setSelectedStamping("Source not defined");
      setSelectedPropagationGraphTab("NR at Dest vs nr apo drop");
      setBucketsOverviewData([]);
      setBucketsOverviewLoading(false);
      fetchNrDestNrApoDropGraphData(
        currentFilters,
        currentUom,
        "Source not defined",
      );
    } else {
      fetchHistoricalGraphData(currentFilters, currentUom);
    }
  };

  useEffect(() => {
    if (!activeAccount?.username) return;

    if (!isInitialLoadDone) return;

    clearDeferredApiTimers();

    setSortingData({
      sortBy: "",
      sortOrder: "DESC",
      fieldsection: "",
    });

    setPaginationData((prev) => ({
      ...prev,
      page: 1,
      rowsPerPage: prev?.rowsPerPage || DEFAULT_ROWS_PER_PAGE,
    }));

    fetchData(
      filters.dcSupplyInsightsFiltersData,
      1,
      "",
      "DESC",
      selectedUOM,
      paginationData?.rowsPerPage || DEFAULT_ROWS_PER_PAGE,
      "",
    );

    fetchGraphForTab(
      activeTab,
      filters.dcSupplyInsightsFiltersData,
      selectedUOM,
    );
  }, [activeTab]);

  const handlePageChange = (
    newPage,
    sortCol,
    sortDir,
    rowsPerPage,
    fieldsection = "",
  ) => {
    const sortBy = sortCol || sortingData.sortBy;
    const sortOrder = sortDir || sortingData.sortOrder;
    setSortingData({ sortBy, sortOrder, fieldsection: fieldsection });
    setPaginationData((prev) => ({ ...prev, page: newPage, rowsPerPage }));
    fetchData(
      filters.dcSupplyInsightsFiltersData,
      newPage,
      sortBy,
      sortOrder,
      selectedUOM,
      rowsPerPage,
      fieldsection,
    );
  };

  const loadFiltersApi = async (
    basePayload,
    loadIntentId,
    shouldShowShimmer,
    prevFiltersData,
  ) => {
    if (shouldShowShimmer) {
      setFiltersLoading(true);
    }

    const signal = cancelPrevious("filters");

    try {
      const res = await fetchDcSupplyInsightsFilters(basePayload, signal);

      if (loadIntentId !== latestLoadIntentRef.current) return;

      setFilters((prev) => ({
        ...prev,
        dcSupplyInsightsFiltersData: mergeFiltersResponse(
          res.filters,
          prevFiltersData,
        ),
        dcSupplyInsightsColumnsMap: res.columns || {},
      }));
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error(error);
      }
    } finally {
      if (loadIntentId === latestLoadIntentRef.current) {
        if (!isInitialLoadDone) {
          setFiltersLoading(false);
          setIsInitialLoadDone(true);
        } else if (shouldShowShimmer) {
          setFiltersLoading(false);
        }
      }
    }
  };

  const loadAllApis = async (currentFilters, uom, options) => {
    const safeFilters = currentFilters || [];
    const safeUom = uom || selectedUOM;
    const safeOptions = options || {};

    const loadIntentId = ++latestLoadIntentRef.current;

    clearDeferredApiTimers();

    const parsedFilters = safeFilters.reduce((acc, f) => {
      acc[f.label] = f.selectedData;
      return acc;
    }, {});

    const basePayload = {
      email: activeAccount.username,
      uom: safeUom,
      filters: parsedFilters,
      type: "Dc Supply Insights",
      overviewtype: activeTab,
    };

    try {
      setLoading((prev) => ({ ...prev, dataLoading: true }));

      if (safeOptions.skipFilters) {
        setFiltersLoading(false);
      } else {
        const shouldShowShimmer =
          !isInitialLoadDone || safeOptions.showFilterShimmer;

        loadFiltersApi(
          basePayload,
          loadIntentId,
          shouldShowShimmer,
          safeFilters,
        );
      }

      fetchData(
        safeFilters,
        1,
        "",
        "DESC",
        safeUom,
        paginationData?.rowsPerPage || DEFAULT_ROWS_PER_PAGE,
        "",
      );

      if (safeOptions.skipHeavyApis) {
        // Skip heavy APIs
      } else {
        scheduleHeavyApis(
          safeFilters,
          safeUom,
          loadIntentId,
          activeTab,
          selectedLossType,
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading((prev) => ({ ...prev, initialLoading: false }));
    }
  };

  const toggleFilterDrawer = useCallback(() => {
    setIsDrawerOpen((prev) => ({ ...prev, filterDrawer: !prev.filterDrawer }));
  }, []);

  const toggleCustomizeDrawer = useCallback(() => {
    setIsDrawerOpen((prev) => ({
      ...prev,
      customiseDrawer: !prev.customiseDrawer,
    }));
  }, []);

  const handleFilterChange = (label, selectedOptions) => {
    const updatedFilters = filters.dcSupplyInsightsFiltersData.map((filter) =>
      filter.label === label
        ? { ...filter, selectedData: selectedOptions }
        : filter,
    );

    setFilters((prev) => ({
      ...prev,
      dcSupplyInsightsFiltersData: updatedFilters,
    }));

    resetHistoricalDateSelection();

    scheduleLoadAllApis(updatedFilters, selectedUOM, {
      skipHeavyApis: false,
      skipFilters: false,
    });
  };

  const handleUOMChange = (label, selectedOptions = "Tonnes") => {
    const newUOM = selectedOptions;

    setSelectedUOM(newUOM);
    resetHistoricalDateSelection();

    scheduleLoadAllApis(
      filters.dcSupplyInsightsFiltersData,
      newUOM,
      {
        skipHeavyApis: false,
        skipFilters: false,
      },
      500,
    );
  };

  const resetFilter = () => {
    const cleared = filters.dcSupplyInsightsFiltersData.map((filter) => ({
      ...filter,
      selectedData: [],
    }));

    setFilters((prev) => ({
      ...prev,
      dcSupplyInsightsFiltersData: cleared,
    }));

    setSelectedUOM("Tonnes");
    resetHistoricalDateSelection();

    scheduleLoadAllApis(
      cleared,
      "Tonnes",
      {
        skipHeavyApis: false,
        skipFilters: false,
      },
      300,
    );
  };

  const handleRemoveSelectedValue = (label, valueToRemove) => {
    const updatedFilters = filters.dcSupplyInsightsFiltersData.map((filter) => {
      if (filter.label !== label) return filter;
      const current = getSelectedValues(filter.selectedData);
      return {
        ...filter,
        selectedData: current.filter(
          (v) => String(v) !== String(valueToRemove),
        ),
      };
    });

    setFilters((prev) => ({
      ...prev,
      dcSupplyInsightsFiltersData: updatedFilters,
    }));

    scheduleLoadAllApis(updatedFilters, selectedUOM, {
      skipHeavyApis: false,
      skipFilters: false,
    });
  };

  const handleExpand = (type) => {
    console.log("Expand Graph:", type);

    setExpandedGraph(type);
    setShowExpanded(false);
  };

  const handleCloseExpanded = () => {
    setExpandedGraph(null);
    setShowExpanded(true);
  };

  const handleCustomization = () => {
    toggleCustomizeDrawer();
  };

  const closeSnackbar = useCallback(() => {
    setSnackBarObject((prev) => ({ ...prev, snackbarOpen: false }));
  }, []);

  const currentColumns =
    filters.customizedColumnsMap?.[activeTab] ||
    filters.dcSupplyInsightsColumnsMap?.[activeTab] ||
    fallbackColumns ||
    [];

  const handleExportToExcel = async () => {
  setLoading((prev) => ({
    ...prev,
    exportLoading: true,
  }));

  try {
    const payload = {
      email: activeAccount.username,
      uom: selectedUOM,
      filters: buildSelectedFilterPayload(
        filters.dcSupplyInsightsFiltersData,
      ),
      type: "Dc Supply Insights",
      overviewtype: activeTab,
      pagination: {
        page: paginationData?.page || 1,
        rowsPerPage:
          paginationData?.rowsPerPage || DEFAULT_ROWS_PER_PAGE,
      },
      sorting: {
        sortBy: sortingData.sortBy || "",
        sortOrder: sortingData.sortOrder || "DESC",
        fieldsection: sortingData.fieldsection || "",
      },
    };

    await fetchDcSupplyInsightsExportData(payload, activeTab);

    setSnackBarObject({
      snackbarOpen: true,
      message: "File exported successfully",
      type: "success",
    });
  } catch (error) {
    console.error("Error exporting DC Supply Insights data:", error);

    setSnackBarObject({
      snackbarOpen: true,
      message: "Error occurred while exporting file.",
      type: "error",
    });
  } finally {
    setLoading((prev) => ({
      ...prev,
      exportLoading: false,
    }));
  }
};

  const selectedNonFavChips = useMemo(
    () =>
      filters.dcSupplyInsightsFiltersData
        .filter((filter) => !filter.isFavourite)
        .flatMap((filter) =>
          getSelectedValues(filter.selectedData).map((value) => ({
            label: filter.label,
            value,
          })),
        ),
    [filters.dcSupplyInsightsFiltersData],
  );

  const handleLossTypeChange = (e) => {
    const lossType = e.target.value;

    setSelectedLossType(lossType);

    fetchAllocToPdqGraphData(
      filters.dcSupplyInsightsFiltersData,
      selectedUOM,
      lossType,
    );
  };

  const handleDropLossTypeChange = (e) => {
    const dropLossType = e.target.value;

    setSelectedDropLossType(dropLossType);

    fetchNrAllocDropGraphData(
      filters.dcSupplyInsightsFiltersData,
      selectedUOM,
      dropLossType,
    );
  };

  const handleStampingChange = (e) => {
    const stamping = e.target.value;

    setSelectedStamping(stamping);

    if (
      activeTab === "NR Propogation Loss" &&
      selectedPropagationGraphTab === "NR at Dest vs nr apo drop"
    ) {
      fetchNrDestNrApoDropGraphData(
        filters.dcSupplyInsightsFiltersData,
        selectedUOM,
        stamping,
      );
    }
  };

  const handlePropagationGraphTabChange = (tab) => {
    const wasExpanded =
      expandedGraph === "nr_dest_nr_apo_drop_graph" ||
      expandedGraph === "buckets_overview_graph";

    setSelectedPropagationGraphTab(tab);

    if (tab === "NR at Dest vs nr apo drop") {
      setBucketsOverviewData([]);
      setBucketsOverviewLoading(false);

      fetchNrDestNrApoDropGraphData(
        filters.dcSupplyInsightsFiltersData,
        selectedUOM,
        selectedStamping,
      );

      if (wasExpanded) {
        setExpandedGraph("nr_dest_nr_apo_drop_graph");
      }
    } else if (tab === "Buckets overview") {
      setNrDestNrApoDropData([]);
      setNrDestNrApoDropLoading(false);

      fetchBucketsOverviewGraphData(
        filters.dcSupplyInsightsFiltersData,
        selectedUOM,
      );

      if (wasExpanded) {
        setExpandedGraph("buckets_overview_graph");
      }
    }
  };

  const renderGraphForActiveTab = () => {
    if (activeTab === "Order Loss NR Sufficiency") {
      return (
        <NrSufficiencyGraph
          data={nrSufficiencyData}
          loading={nrSufficiencyLoading}
        />
      );
    }
    if (activeTab === "NR - Alloc % Drop") {
      return (
        <NrAllocDropGraph
          data={nrAllocDropData}
          loading={nrAllocDropLoading}
          selectedDropLossType={selectedDropLossType}
        />
      );
    }
    if (activeTab === "Alloc to PDQ") {
      return (
        <AllocToPdqGraph
          data={allocToPdqData}
          loading={allocToPdqLoading}
          selectedLossType={selectedLossType}
        />
      );
    }
    if (activeTab === "NR Propogation Loss") {
      if (selectedPropagationGraphTab === "NR at Dest vs nr apo drop") {
        return (
          <NrDestNrApoDropGraph
            data={nrDestNrApoDropData}
            loading={nrDestNrApoDropLoading}
            selectedStamping={selectedStamping}
          />
        );
      }
      return (
        <BucketsOverviewGraph
          data={bucketsOverviewData}
          loading={bucketsOverviewLoading}
        />
      );
    }
    return (
      <HistoricalDataGraph data={historicalData} loading={historicalLoading} />
    );
  };

  const renderPropagationExpandedGraph = () => {
    if (selectedPropagationGraphTab === "NR at Dest vs nr apo drop") {
      return (
        <NrDestNrApoDropGraph
          data={nrDestNrApoDropData}
          loading={nrDestNrApoDropLoading}
          selectedStamping={selectedStamping}
        />
      );
    }
    return (
      <BucketsOverviewGraph
        data={bucketsOverviewData}
        loading={bucketsOverviewLoading}
      />
    );
  };

  const getContainerClassName = () => {
    return `${styles.container} ${
      loading.exportLoading ? styles.blurBackground : ""
    }`;
  };

  const getPropogationTabStyle = () => {
    return `${styles.propagationTab} ${
      selectedPropagationGraphTab === "NR at Dest vs nr apo drop"
        ? styles.activePropagationTab
        : ""
    }`;
  };

  const getBucketsOverviewTabStyle = () => {
    return `${styles.propagationTab} ${
      selectedPropagationGraphTab === "Buckets overview"
        ? styles.activePropagationTab
        : ""
    }`;
  };

  const getPropagationGraphTitle = () => {
    return selectedPropagationGraphTab === "NR at Dest vs nr apo drop"
      ? "NR at Dest vs nr apo drop"
      : "Buckets Overview";
  };

  const getBucketsOverviewTabClassName = () => {
    if (selectedPropagationGraphTab === "Buckets overview") {
      return `${styles.propagationTab} ${styles.activePropagationTab}`;
    }
    return styles.propagationTab;
  };

  return (
    <>
      {loading.initialLoading ? (
        <div className={styles.loaderContainer}>
          <CircularProgress />
        </div>
      ) : (
        <div className={getContainerClassName()}>
          <div className={styles.sectionContainer}>
            {filtersLoading ? (
              <div className={styles.filterShimmerWrapper}>
                {Array.from({ length: 8 }, (_, i) => `filter-shimmer-${i}`).map(
                  (shimmerKey) => (
                    <div key={shimmerKey} className={styles.filterShimmer} />
                  ),
                )}
              </div>
            ) : (
              <>
                {filters["dcSupplyInsightsFiltersData"]
                  .filter((filter) => filter.isFavourite)
                  .map((filter, index) => (
                    <div
                      key={`${filter.label}-${index}`}
                      className={styles.filterWrapper}
                    >
                      <MultiSelectWithSearchInput
                        key={`${filter.label}-${index}`}
                        label={filter.label}
                        options={filter.data}
                        filterValues={filter.selectedData}
                        isMultiSelect={filter.isMultiSelect}
                        handleFilter={handleFilterChange}
                        type="Dc Supply Insights"
                        defaultValue={filter.defaultValue || []}
                      />
                    </div>
                  ))}

                <div className={styles.filterWrapper}>
                  <MultiSelectWithSearchInput
                    label="Units"
                    options={uomOptions}
                    filterValues={[selectedUOM]}
                    isMultiSelect={false}
                    handleFilter={handleUOMChange}
                  />
                </div>
                <div className={styles.iconContainer}>
                  <button
                    type="button"
                    className={styles.iconButton}
                    aria-label="Refresh filters"
                    onClick={resetFilter}
                  >
                    <img src={Refresh} className={styles.iconStyling} alt="" />
                  </button>
                  <OverflowMenu
                    menuItems={[
                      {
                        label: "More Filters",
                        icon: Filters,
                        onClick: toggleFilterDrawer,
                      },
                    ]}
                    Icon={Threedots}
                  />
                </div>
              </>
            )}
          </div>

          {selectedNonFavChips.length > 0 && (
            <div className={styles.selectedChipsRow}>
              {selectedNonFavChips.map((chip) => (
                <div
                  key={`${chip.label}-${String(chip.value)}`}
                  className={styles.selectedChip}
                  title={`${chip.label}: ${chip.value}`}
                >
                  <span className={styles.selectedChipLabel}>
                    {chip.label}:
                  </span>
                  <span className={styles.selectedChipValue}>
                    {String(chip.value)}
                  </span>
                  <button
                    type="button"
                    className={styles.selectedChipClose}
                    aria-label={`Remove ${chip.label} ${chip.value}`}
                    onClick={() =>
                      handleRemoveSelectedValue(chip.label, chip.value)
                    }
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className={styles.scrollableContentWrapper}>
            {!loading.initialLoading && (
              <div className={styles.placeholderWrapper}>
              <div className={styles.card}>
                <div className={styles.header}>
                  <span className={styles.titleName}>NR Mitigation</span>
                  {showExpanded && (
                    <div className={styles.segmentedBtn}>
                      <button
                        type="button"
                        className={styles.segment}
                        style={{
                          padding: "0px",
                          background: "white",
                          cursor: "pointer",
                          position: "relative",
                        }}
                        title="Expand / Zoom"
                        onClick={() => handleExpand("buckets_allocation")}
                      >
                        <img
                          src={Expand}
                          alt="Expand"
                          className={styles.expandIconCss}
                        />
                      </button>
                    </div>
                  )}
                </div>
                <BucketsOfAllocation
                  apiData={{ data: pdqTableData }}
                  loading={pdqLoading}
                  loadMoreLoading={pdqLoadMoreLoading}
                  hasMore={pdqHasMore}
                  onLoadMore={handlePdqLoadMore}
                  showExpanded={showExpanded}
                  sortingData={pdqSortingData}
                  onSortChange={handlePdqSortChange}
                />
              </div>

              <div className={styles.card}>
                <div className={styles.header}>
                  <span className={styles.titleName}>
                    {getGraphTitleForTab(
                      activeTab,
                      selectedPropagationGraphTab,
                    )}
                  </span>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    {activeTab === "NR Propogation Loss" && (
                      <>
                        {selectedPropagationGraphTab ===
                          "NR at Dest vs nr apo drop" && (
                          <div className={styles.dropdownContainer}>
                            <select
                              value={selectedStamping}
                              onChange={handleStampingChange}
                            >
                              {stampingOptions.map((item) => (
                                <option key={item} value={item}>
                                  {item}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div className={styles.propagationTabs}>
                          <button
                            type="button"
                            className={getPropogationTabStyle()}
                            onClick={() =>
                              handlePropagationGraphTabChange(
                                "NR at Dest vs nr apo drop",
                              )
                            }
                          >
                            NR at Dest vs nr apo drop
                          </button>

                          <button
                            type="button"
                            className={getBucketsOverviewTabStyle()}
                            onClick={() =>
                              handlePropagationGraphTabChange(
                                "Buckets overview",
                              )
                            }
                          >
                            Buckets overview
                          </button>
                        </div>
                      </>
                    )}
                    {activeTab === "Alloc to PDQ" && (
                      <div className={styles.dropdownContainer}>
                        <select
                          value={selectedLossType}
                          onChange={handleLossTypeChange}
                        >
                          {allocToPdqLossOptions.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {activeTab === "NR - Alloc % Drop" && (
                      <div className={styles.dropdownContainer}>
                        <select
                          value={selectedDropLossType}
                          onChange={handleDropLossTypeChange}
                        >
                          {nrAllocDropLossOptions.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {activeTab === "Summary" && showExpanded && (
                      <div
                        className={styles.historicalCalendarWrapper}
                        ref={calendarWrapperRef}
                      >
                        <button
                          type="button"
                          className={styles.historicalCalendarButton}
                          onClick={handleHistoricalCalendarToggle}
                          title="Select date range"
                          aria-label="Select date range for historical data"
                        >
                          <img
                            src={CalendarIcon}
                            alt="Calendar"
                            className={styles.calendarIconCss}
                          />
                        </button>
                        {isHistoricalCalendarOpen && (
                          <HistoricalCalendarPopover
                            pendingHistoricalDateRange={
                              pendingHistoricalDateRange
                            }
                            parseYYYYMMDD={parseYYYYMMDD}
                            formatDateToYYYYMMDD={formatDateToYYYYMMDD}
                            isHistoricalDateAvailable={
                              isHistoricalDateAvailable
                            }
                            handleHistoricalDateClick={
                              handleHistoricalDateClick
                            }
                            styles={styles}
                          />
                        )}
                      </div>
                    )}

                    {showExpanded && (
                      <div className={styles.segmentedBtn}>
                        <button
                          type="button"
                          className={styles.segment}
                          style={{
                            padding: "0px",
                            background: "white",
                            cursor: "pointer",
                            position: "relative",
                          }}
                          title="Expand / Zoom"
                          onClick={() =>
                            handleExpand(
                              getGraphTypeForTab(
                                activeTab,
                                selectedPropagationGraphTab,
                              ),
                            )
                          }
                        >
                          <img
                            src={Expand}
                            alt="Expand"
                            className={styles.expandIconCss}
                          />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div>{renderGraphForActiveTab()}</div>
              </div>
            </div>
          )}

          {expandedGraph === "buckets_allocation" && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                <div className={styles.expandedHeader}>
                  <span>NR Mitigation</span>
                  <button
                    type="button"
                    className={styles.closeButton}
                    onClick={handleCloseExpanded}
                  >
                    ✕
                  </button>
                </div>

                <div className={styles.expandedTableWrapper}>
                  <BucketsOfAllocation
                    apiData={{ data: pdqTableData }}
                    loading={pdqLoading}
                    loadMoreLoading={pdqLoadMoreLoading}
                    hasMore={pdqHasMore}
                    onLoadMore={handlePdqLoadMore}
                    sortingData={pdqSortingData}
                    onSortChange={handlePdqSortChange}
                  />
                </div>
              </div>
            </div>
          )}

          {expandedGraph === "historical_graph" && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                <div className={styles.expandedHeader}>
                  <span>Historical data (NR to PDQ)</span>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "20px",
                    }}
                  >
                    <div
                      className={styles.historicalCalendarWrapper}
                      ref={calendarWrapperRef}
                    >
                      <button
                        type="button"
                        className={styles.historicalCalendarButton}
                        onClick={handleHistoricalCalendarToggle}
                        title="Select date range"
                        aria-label="Select date range for historical data"
                      >
                        <img
                          src={CalendarIcon}
                          alt="Calendar"
                          className={styles.calendarIconCss}
                        />
                      </button>
                      {isHistoricalCalendarOpen && (
                        <HistoricalCalendarPopover
                          pendingHistoricalDateRange={
                            pendingHistoricalDateRange
                          }
                          parseYYYYMMDD={parseYYYYMMDD}
                          formatDateToYYYYMMDD={formatDateToYYYYMMDD}
                          isHistoricalDateAvailable={isHistoricalDateAvailable}
                          handleHistoricalDateClick={handleHistoricalDateClick}
                          styles={styles}
                        />
                      )}
                    </div>

                    <button
                      type="button"
                      className={styles.closeButton}
                      onClick={handleCloseExpanded}
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <div className={styles.expandedTableWrapper}>
                  <HistoricalDataGraph
                    data={historicalData}
                    loading={historicalLoading}
                  />
                </div>
              </div>
            </div>
          )}

          {(expandedGraph === "nr_dest_nr_apo_drop_graph" ||
            expandedGraph === "buckets_overview_graph") && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                <div className={styles.expandedHeader}>
                  <span>{getPropagationGraphTitle()}</span>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "20px",
                    }}
                  >
                    {selectedPropagationGraphTab ===
                      "NR at Dest vs nr apo drop" && (
                      <div className={styles.dropdownContainer}>
                        <select
                          value={selectedStamping}
                          onChange={handleStampingChange}
                        >
                          {stampingOptions.map((item) => (
                            <option key={item} value={item}>
                              {item}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div className={styles.propagationTabs}>
                      <button
                        type="button"
                        className={`${styles.propagationTab} ${
                          selectedPropagationGraphTab ===
                          "NR at Dest vs nr apo drop"
                            ? styles.activePropagationTab
                            : ""
                        }`}
                        onClick={() =>
                          handlePropagationGraphTabChange(
                            "NR at Dest vs nr apo drop",
                          )
                        }
                      >
                        NR at Dest vs nr apo drop
                      </button>

                      <button
                        type="button"
                        className={getBucketsOverviewTabClassName()}
                        onClick={() =>
                          handlePropagationGraphTabChange("Buckets overview")
                        }
                      >
                        Buckets overview
                      </button>
                    </div>

                    <button
                      type="button"
                      className={styles.closeButton}
                      onClick={handleCloseExpanded}
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <div className={styles.expandedTableWrapper}>
                  {renderPropagationExpandedGraph()}
                </div>
              </div>
            </div>
          )}

          {expandedGraph === "nr_sufficiency_graph" && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                <div className={styles.expandedHeader}>
                  <span>NR Sufficiency</span>

                  <button
                    type="button"
                    className={styles.closeButton}
                    onClick={handleCloseExpanded}
                  >
                    ✕
                  </button>
                </div>

                <div className={styles.expandedTableWrapper}>
                  <NrSufficiencyGraph
                    data={nrSufficiencyData}
                    loading={nrSufficiencyLoading}
                  />
                </div>
              </div>
            </div>
          )}

          {expandedGraph === "nr_alloc_drop_graph" && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                <div className={styles.expandedHeader}>
                  <span>NR APO to Alloc at source</span>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "20px",
                    }}
                  >
                    <div className={styles.dropdownContainer}>
                      <select
                        value={selectedDropLossType}
                        onChange={handleDropLossTypeChange}
                      >
                        {nrAllocDropLossOptions.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="button"
                      className={styles.closeButton}
                      onClick={handleCloseExpanded}
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <div className={styles.expandedTableWrapper}>
                  <NrAllocDropGraph
                    data={nrAllocDropData}
                    loading={nrAllocDropLoading}
                    selectedDropLossType={selectedDropLossType}
                  />
                </div>
              </div>
            </div>
          )}

          {expandedGraph === "alloc_to_pdq_graph" && (
            <div className={styles.expandedOverlay}>
              <div className={styles.expandedContainer}>
                <div className={styles.expandedHeader}>
                  <span>Alloc To PDQ</span>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "20px",
                    }}
                  >
                    <div className={styles.dropdownContainer}>
                      <select
                        value={selectedLossType}
                        onChange={handleLossTypeChange}
                      >
                        {allocToPdqLossOptions.map((item) => (
                          <option key={item} value={item}>
                            {item}
                          </option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="button"
                      className={styles.closeButton}
                      onClick={handleCloseExpanded}
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <div className={styles.expandedTableWrapper}>
                  <AllocToPdqGraph
                    data={allocToPdqData}
                    loading={allocToPdqLoading}
                    selectedLossType={selectedLossType}
                  />
                </div>
              </div>
            </div>
          )}

          <div className={styles.intrMainBody}>
            <div className={styles.intrHeader}>
              <div>Overview</div>

              <div className={styles.customButtonGroup}>
                <OverviewTabs
                  selectedTab={activeTab}
                  onTabChange={setActiveTab}
                />
                <CustomButton
                  icon={Expand}
                  label="Customise"
                  type="mm-save"
                  onClick={handleCustomization}
                />
                <CustomButton
                  icon={newExcel}
                  label="Export"
                  type="mm-save"
                  onClick={handleExportToExcel}
                />
                <div className={styles.segmentedBtnOverview}>
                  <button
                    type="button"
                    className={styles.segment}
                    style={{
                      padding: "6px",
                      background: "white",
                      cursor: "pointer",
                    }}
                    onClick={() => handleExpand("table")}
                    title="Expand / Zoom"
                  >
                    <img
                      src={Expand}
                      alt="Expand"
                      className={styles.expandIconOverview}
                    />
                  </button>
                </div>
              </div>
            </div>
            <DcSupplyInsightsOverviewTable
              data={tableData}
              columns={currentColumns.length ? currentColumns : null}
              paginationData={paginationData}
              sortingData={sortingData}
              handlePageChange={handlePageChange}
              isLoading={loading.dataLoading || loading.tableBodyLoading}
              activeTab={activeTab}
            />
          </div>
          </div>

          {isDrawerOpen.filterDrawer && (
            <MoreFilters
              anchor="right"
              filterDrawer={isDrawerOpen.filterDrawer}
              handleFilterDrawer={toggleFilterDrawer}
              rmpmFilters={filters["dcSupplyInsightsFiltersData"]}
              type={`Dc Supply Insights (${activeTab})`}
              setRmpmFilters={(updatedFilters) => {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  dcSupplyInsightsFiltersData: updatedFilters,
                }));

                scheduleLoadAllApis(
                  updatedFilters,
                  selectedUOM,
                  {
                    skipHeavyApis: false,
                    skipFilters: false,
                  },
                  300,
                );
              }}
              username={activeAccount.username}
              limit={8}
            />
          )}

          {isDrawerOpen.customiseDrawer && (
            <CustomiseColumns
              anchor="right"
              columns={[...currentColumns]}
              isOpen={isDrawerOpen.customiseDrawer}
              handleCustomizeDrawer={toggleCustomizeDrawer}
              columnCustomization={(customizedColumns) => {
                setFilters((prev) => ({
                  ...prev,
                  customizedColumnsMap: {
                    ...prev.customizedColumnsMap,
                    [activeTab]: customizedColumns,
                  },
                }));

                fetchData(
                  filters.dcSupplyInsightsFiltersData,
                  paginationData?.page || 1,
                  sortingData.sortBy || "",
                  sortingData.sortOrder || "DESC",
                  selectedUOM,
                  paginationData?.rowsPerPage || 15,
                  sortingData.fieldsection || "",
                );

                toggleCustomizeDrawer();
              }}
              username={activeAccount.username}
              type={`Dc Supply Insights (${activeTab})`}
            />
          )}
        </div>
      )}

      {loading.exportLoading && (
        <div className={styles.exportOverlay}>
          <div className={styles.exportLoader}>
            <CircularProgress />
            <div style={{ marginTop: 10, fontSize: 14 }}>Exporting file...</div>
          </div>
        </div>
      )}
      {expandedGraph === "table" && (
        <div className={styles.expandedOverlay}>
          <div className={styles.expandedContainer}>
            <div className={styles.expandedHeader}>
              <span>Dc Supply Insights Overview</span>
              <button
                type="button"
                className={styles.closeButton}
                onClick={handleCloseExpanded}
              >
                ✕
              </button>
            </div>

            <div className={styles.expandedTableWrapper}>
              <DcSupplyInsightsOverviewTable
                data={tableData}
                columns={currentColumns.length ? currentColumns : null}
                paginationData={paginationData}
                sortingData={sortingData}
                handlePageChange={handlePageChange}
                isLoading={loading.dataLoading || loading.tableBodyLoading}
                activeTab={activeTab}
              />
            </div>
          </div>
        </div>
      )}
      {snackBarObject.snackbarOpen && (
        <SnackBarMessage
          type={snackBarObject.type}
          snackbarMessage={snackBarObject.message}
          snackbarOpen={snackBarObject.snackbarOpen}
          setSnackbarOpen={closeSnackbar}
        />
      )}
    </>
  );
};

DcSupplyInsights.propTypes = {
  sideViewTab: PropTypes.string,
};

DcSupplyInsights.defaultProps = {
  sideViewTab: "",
};

export default DcSupplyInsights;
